import { createSelector } from 'reselect'
import type {
  EmailCampaign,
  EmailCampaignId,
  EmailCampaignSchedule,
} from 'types/campaign'
import type { Campaign, CampaignFooter } from 'types/content-editor'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import type { State as CampaignsFooterState } from '@disco/state-management/reducers/email-campaigns/campaign-footers'
import type { State as CampaignListState } from '@disco/state-management/reducers/email-campaigns/campaign-list'
import type { State as RecipientRulesState } from '@disco/state-management/reducers/email-campaigns/recipient-rules'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getContactsFilteredByCampaignRules,
  isContactsLoaded,
} from '@disco/state-management/selectors/contacts-all'
import { getEmailGroups } from '@disco/state-management/selectors/emailGroups'
import { getUser } from '@disco/state-management/selectors/user'
import {
  formatScheduledCampaignData,
  getCampaignSenderWithDefaults,
  getTemplateData,
  isCampaignLoaded,
  resolveCampaignBusinessAddress,
  searchCampaigns,
} from '@disco/utils/emailCampaigns'
export const MAXIMUM_RECIPIENTS = 10000
/**
 * Gets all email campaigns with loading state.
 * @param state - The Redux state
 * @returns Object containing loading status and array of email campaigns
 */
export function getCampaigns(state: State): {
  loading: boolean
  loaded: boolean
  emailCampaigns: Array<EmailCampaign>
} {
  return {
    ...state.entities.emailCampaigns,

    emailCampaigns: Object.values(state.entities.emailCampaigns.emailCampaigns),
  }
}
/**
 * Gets a specific email campaign by ID.
 * @param state - The Redux state
 * @param props - Object containing the campaign ID
 * @param props.campaignId - The campaign ID to retrieve
 * @returns The email campaign object
 */
export function getCampaign(
  state: State,
  {
    campaignId,
  }: {
    campaignId: EmailCampaignId
  },
): EmailCampaign {
  return state.entities.emailCampaigns.emailCampaigns[campaignId]
}
export function getCampaignBusinessAddress(
  state: State,
  { campaignId }: { campaignId: EmailCampaignId },
): ReturnType<typeof resolveCampaignBusinessAddress> {
  return resolveCampaignBusinessAddress(
    getCampaign(state, { campaignId }),
    state.user?.business,
  )
}
/**
 * Whether a campaign has edits that are still worth writing to the server.
 * @param state - The Redux state
 * @param props - Object containing the campaign ID
 * @param props.campaignId - The campaign ID to check
 * @returns True when the campaign is dirty and can still be saved
 */
export function hasUnsavedCampaignEdits(
  state: State,
  {
    campaignId,
  }: {
    campaignId: EmailCampaignId
  },
): boolean {
  const campaign = getCampaign(state, { campaignId })
  // a conflict waits on the user, so saving again only repeats the modal
  return Boolean(
    campaign?.dirty && campaign.status !== 'sent' && !campaign.conflicting,
  )
}
/**
 * Gets the campaign list state.
 * @param state - The Redux state
 * @returns The campaign list state object
 */
export function getCampaignListState(state: State): CampaignListState {
  return state.emailCampaigns.campaignList
}
/**
 * Gets the campaign recipient rules state.
 * @param state - The Redux state
 * @returns The recipient rules state object
 */
export function getCampaignRules(state: State): RecipientRulesState {
  return state.emailCampaigns.recipientRules
}
/**
 * Gets the current campaign search query.
 * @param state - The Redux state
 * @returns The search query string, or null/undefined if none
 */
export const getCampaignSearchQuery = (
  state: State,
): string | null | undefined => {
  return state.emailCampaigns.campaignSearch.q
}
/**
 * Gets campaigns list sorted and filtered by type, search query, and status.
 * @param state - The Redux state
 * @param status - Optional status filter
 * @returns Filtered and sorted campaigns object
 */
export const getCampaignListSortedFiltered = createSelector(
  getCampaigns,
  getCampaignListState,
  getCampaignSearchQuery,
  (_state, status) => status,
  (
    emailCampaigns: ReturnType<typeof getCampaigns>,
    listState: CampaignListState,
    q: string | null | undefined,
    status: string | undefined,
  ) => {
    let campaigns = emailCampaigns.emailCampaigns

    if (listState.typeFilter !== null) {
      campaigns = campaigns.filter(c => c.type === listState.typeFilter)
    }

    return {
      ...emailCampaigns,
      emailCampaigns: searchCampaigns(campaigns, q, status as any),
    }
  },
)
/**
 * Gets the appropriate link URL for a campaign based on its status.
 * @param campaign - The email campaign object
 * @returns URL path for the campaign (stats, confirm, or recipients)
 */
export function getCampaignLink(campaign: EmailCampaign): string {
  switch (campaign.status) {
    case 'sent':
      return `/campaigns/${campaign.id}/stats`

    case 'scheduled':
      return `/campaigns/${campaign.id}/confirm`

    default:
      return `/campaigns/${campaign.id}/recipients`
  }
}
/**
 * Gets the campaign editor state.
 * @param state - The Redux state
 * @returns The campaign editor state object
 */
export const getCampaignEditorState = (state: State): Campaign => {
  return state.emailCampaigns.campaignEditor
}
/**
 * Gets the currently edited section from the campaign editor.
 * @param state - The Redux state
 * @returns Object with section index and section data, or null if no section is being edited
 */
export const getCampaignEditedSection = createSelector(
  getCampaignEditorState,
  editor => {
    const sectionIndex = editor.sections.findIndex(s => s.editMode)
    if (sectionIndex < 0) return null
    return {
      sectionIndex,
      section: editor.sections[sectionIndex],
    }
  },
)
/**
 * Gets the campaign footers state.
 * @param state - The Redux state
 * @returns The campaign footers state object
 */
export const getCampaignFooters = (state: State): CampaignsFooterState =>
  state.emailCampaigns.campaignFooters
/**
 * Gets the default campaign footer.
 * Returns the only footer if there's one, or the newest footer if there are multiple.
 * @param state - The Redux state
 * @returns The default campaign footer, or null if none available
 */
export const getDefaultCampaignFooter = (
  state: State,
): CampaignFooter | null | undefined => {
  const items = getCampaignFooters(state).items

  if (items.length === 1) {
    return items[0]
  } else if (items.length > 1) {
    const sortedFooters = [...items].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
    return sortedFooters[0]
  }

  return null
}
/**
 * Gets a specific playlist from a campaign.
 * @param state - The Redux state
 * @param props - Object containing campaign and playlist IDs
 * @param props.campaignId - The campaign ID
 * @param props.playlistId - The playlist ID to retrieve from the campaign
 * @returns The playlist object from the campaign, or null if not found
 */
export const getCampaignPlaylist = (
  state: State,
  {
    campaignId,
    playlistId,
  }: {
    campaignId: EmailCampaignId
    playlistId: PlaylistId
  },
): Playlist | null | undefined => {
  const campaign = getCampaign(state, {
    campaignId,
  })
  const playlist =
    campaign && campaign.playlists ? campaign.playlists[playlistId] : null
  return playlist
}
/**
 * Gets validation status for all campaign steps (subject, recipients, address, sender, template, content).
 * @param state - The Redux state
 * @param campaignId - The campaign ID to validate
 * @returns Object containing validation status for each step and overall validity
 */
export const getValidCampaignSteps = createSelector(
  (state, campaignId) =>
    getCampaign(state, {
      campaignId: parseInt(campaignId),
    }),
  getUser,
  state => getContactsFilteredByCampaignRules(state),
  getEmailGroups,
  getCampaignEditorState,
  (
    campaign: ReturnType<typeof getCampaign>,
    user: ReturnType<typeof getUser>,
    { filteredContacts }: { filteredContacts: any[] },
    emailGroups: ReturnType<typeof getEmailGroups>,
    editor: ReturnType<typeof getCampaignEditorState>,
  ) => {
    const template_data = getTemplateData(campaign)
    const { sender_name, sender_email } = getCampaignSenderWithDefaults(
      campaign,
      user,
      emailGroups,
    )
    const validSubject = Boolean(template_data.subject)
    const validRecipients = filteredContacts.length > 0
    const validAddress = Boolean(
      resolveCampaignBusinessAddress(campaign, user.business)
        .business_address_mjml,
    )
    const validSender = Boolean(sender_name && sender_email)
    const validTemplate = Boolean(campaign.template)
    const validContent = Boolean(template_data.template_content_saved)
    const valid = Boolean(
      validSubject &&
        validRecipients &&
        validAddress &&
        validSender &&
        validTemplate &&
        validContent,
    )
    const sendAllowed = isCampaignLoaded(editor, campaign.playlists)
    return {
      validSubject,
      validRecipients,
      validAddress,
      validSender,
      valid,
      sendAllowed,
      validTemplate,
      validContent,
    }
  },
)
/**
 * Checks if a campaign is allowed to be sent based on validation, feature availability, and recipient count.
 * @param state - The Redux state
 * @param campaignId - The campaign ID to check
 * @returns True if the campaign can be sent, false otherwise
 */
export const isCampaignSendAllowed = createSelector(
  (state, campaignId) => getValidCampaignSteps(state, campaignId),
  isContactsLoaded,
  getContactsFilteredByCampaignRules,
  state =>
    isFeatureEnabled(state, {
      featureName: EMAIL_CAMPAIGN_BUILDER,
    }),
  (
    { valid, sendAllowed },
    contactsLoaded,
    { filteredContacts },
    isFeatureEnabled,
  ) => {
    const countContacts = filteredContacts.length
    const disableSend =
      !valid ||
      !sendAllowed ||
      !isFeatureEnabled ||
      countContacts > MAXIMUM_RECIPIENTS ||
      !contactsLoaded
    return !disableSend
  },
)
/**
 * Gets the schedule information for a campaign.
 * @param state - The Redux state
 * @param campaignId - The campaign ID
 * @returns Formatted campaign schedule data, or null if campaign is not scheduled
 */
export const getCampaignSchedule = (
  state: State,
  campaignId: number,
): EmailCampaignSchedule | null | undefined => {
  const campaign = getCampaign(state, {
    campaignId,
  })
  if (!campaign || !campaign.scheduled_at || !campaign.scheduled_at_timezone)
    return null
  const campaignFormattedData = formatScheduledCampaignData(campaign)
  return campaignFormattedData
}
